import type { Topic } from "@circle-react/types/Topic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MAX_TOPICS_PER_POST } from "../constants";
import { sortTopics } from "../utils";
import { useCreateTopic } from "./useCreateTopic";
import { useTopics } from "./useTopics";

interface UseTopicsManagementProps {
  canRemoveTopic: (topic: Topic) => boolean;
  maxTopicsAllowed?: number;
  onChange?: (value: number[]) => void;
  onTopicCreated?: (topic: Topic) => void;
  sortable?: boolean;
  spaceId?: string;
  value: number[];
}

export const useTopicsManager = ({
  spaceId,
  value,
  onChange,
  onTopicCreated,
  maxTopicsAllowed = MAX_TOPICS_PER_POST,
  sortable = false,
  canRemoveTopic,
}: UseTopicsManagementProps) => {
  const [preselectedTopicIds, setPreselectedTopicIds] =
    useState<number[]>(value);
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const previousSpaceId = useRef(spaceId);
  const isChangingSpaces = previousSpaceId.current !== spaceId;

  useEffect(() => {
    if (previousSpaceId.current === spaceId) return;

    previousSpaceId.current = spaceId;
    setSelectedTopics([]);
    setPreselectedTopicIds(value);
    onChange?.([]);
    setIsInitialized(false);
  }, [onChange, spaceId, value]);

  // Loads the preselected topics or the first page of topics if no preselected
  // topics are provided. This is used to set the initial selected topics.
  const { data: topicsData } = useTopics({
    spaceId,
    ids: preselectedTopicIds,
  });

  // Check topic availability without filtering by the preselected topic IDs.
  const { data: topicsForPickerData } = useTopics({ spaceId });

  const { mutate: createTopic, isPending: isCreatingTopic } = useCreateTopic({
    spaceId,
    onSuccess: (newTopic: Topic) => {
      setSelectedTopics(prev => [...prev, newTopic]);
      onTopicCreated?.(newTopic);
    },
  });

  const selectedTopicIds = useMemo(
    () => selectedTopics.map(topic => topic.id),
    [selectedTopics],
  );

  const updateSelectedTopics = useCallback(
    (topics: Topic[]) => {
      setSelectedTopics(sortable ? topics : sortTopics(topics));
    },
    [sortable],
  );

  // Populate selected topics from the initial API response
  useEffect(() => {
    if (isInitialized || !topicsData?.records) return;

    const hasPreselectedTopics = preselectedTopicIds.length > 0;
    const selectedTopicRecords = hasPreselectedTopics
      ? [...topicsData.records].sort(
          (a, b) =>
            preselectedTopicIds.indexOf(a.id) -
            preselectedTopicIds.indexOf(b.id),
        )
      : [];

    updateSelectedTopics(selectedTopicRecords);
    setIsInitialized(true);
  }, [isInitialized, preselectedTopicIds, topicsData, updateSelectedTopics]);

  // Propagate selected topic updates to parent component
  useEffect(() => {
    if (!isInitialized || isChangingSpaces) return;

    onChange?.(selectedTopicIds);
  }, [isChangingSpaces, isInitialized, onChange, selectedTopicIds]);

  const removeTopic = useCallback((topic: Topic) => {
    setSelectedTopics(prev => prev.filter(t => t.id !== topic.id));
  }, []);

  const removeAllTopics = useCallback(() => {
    setSelectedTopics(prev => prev.filter(t => !canRemoveTopic(t)));
  }, [canRemoveTopic]);

  const removeLastTopic = useCallback(() => {
    const lastTopic = selectedTopics[selectedTopics.length - 1];

    if (!lastTopic || !canRemoveTopic(lastTopic)) return;

    removeTopic(lastTopic);
  }, [selectedTopics, canRemoveTopic, removeTopic]);

  const hasTopicsForPicker = topicsForPickerData
    ? topicsForPickerData.count > 0
    : false;
  const hasTopicsSelected = selectedTopics.length > 0;
  const hasMaxTopicsSelected = selectedTopics.length >= maxTopicsAllowed;

  return {
    selectedTopics,
    selectedTopicIds,
    updateSelectedTopics,
    hasTopicsForPicker,
    hasTopicsSelected,
    hasMaxTopicsSelected,
    createTopic,
    isCreatingTopic,
    removeTopic,
    removeAllTopics,
    removeLastTopic,
  };
};
